import React, { useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { FlexyIconButton, ModalContext } from '@europrocurement/flexy-components';
import { editIcon } from '@europrocurement/l2d-icons';
import { SousClientApi } from '@europrocurement/l2d-domain';
import { PrescriberClientForm } from '@europrocurement/l2d-modules/modules/PrescriberClient';

interface HeaderTitleWithEditProps {
    clientOpereRaisonSociale: string;
    sousClientSelected?: SousClientApi;
    onSuccess?: () => void;
}

export const HeaderTitleWithEdit: React.FunctionComponent<HeaderTitleWithEditProps> = function ({
    clientOpereRaisonSociale,
    sousClientSelected,
    onSuccess,
}) {
    const { modalActions } = React.useContext(ModalContext);
    const [titleHovered, setTitleHovered] = useState(false);

    const handleCancel = useCallback(() => {
        modalActions.close();
    }, [modalActions]);

    const handleSuccess = useCallback(async () => {
        try {
            modalActions.close();
            onSuccess?.();
        } catch (error) {
            console.error('Erreur lors de la mise à jour:', error);
        }
    }, [modalActions, onSuccess]);

    const openPrescriberClientForm = useCallback(
        (sousClient: SousClientApi) => {
            modalActions.call(
                <PrescriberClientForm
                    onCancel={handleCancel}
                    onSuccess={handleSuccess}
                    sousClient={sousClient}
                />,
            );
        },
        [handleCancel, handleSuccess, modalActions],
    );

    const editFolio = useCallback(() => {
        if (!sousClientSelected) return;
        openPrescriberClientForm(sousClientSelected);
    }, [openPrescriberClientForm, sousClientSelected]);

    const titleWrapperStyle = {
        display: 'flex',
        flexDirection: 'row',
        gap: '0 8px',
        alignItems: 'center',
        justifyContent: 'flex-start',
        marginBottom: '0',
    };

    const titleStyle = {
        maxWidth: '75%',
        overflow: 'hidden',
        textWrap: 'nowrap',
        textOverflow: 'ellipsis',
    };

    return (
        <Box sx={titleWrapperStyle}>
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    minHeight: '24px',
                }}
                onMouseEnter={() => setTitleHovered(true)}
                onMouseLeave={() => setTitleHovered(false)}
            >
                <Typography
                    variant="l2dh5"
                    sx={titleStyle}
                >
                    {clientOpereRaisonSociale}
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        gap: '4px',
                        opacity: titleHovered ? 1 : 0,
                        transform: titleHovered ? 'translateX(0)' : 'translateX(-10px)',
                        transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    }}
                >
                    <FlexyIconButton
                        displayTooltip
                        icon={editIcon}
                        onClick={editFolio}
                        tooltipOverwriteProps={{
                            title: 'Éditer la société opérée',
                        }}
                        iconOverwriteProps={{
                            size: '2xs',
                            sx: {
                                '&:hover': {
                                    color: 'primary.main',
                                },
                            },
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};
